/**
 * The MCP servers the MCP & CLI page lists besides the portal's own (DX-501,
 * specs/mcp-and-cli.md).
 *
 * The portal's own tools are read from its endpoint, so they cannot claim a
 * tool that is not there. These cannot be: each sits behind a sign-in of its
 * own, and a page cannot ask them on a reader's behalf. So they are written
 * down here — checked on 15 October 2026 by asking every endpoint without
 * credentials, and confirmed in DX-501 — and a server changes state by a pull
 * request to this file.
 *
 * Left out on purpose: Atlas, which the portal's own server replaces (DX-274),
 * and the servers that answer Aria and the tenants rather than a colleague's
 * Claude (PE-81) — among them Roadie's, which only the ai-gateway can reach.
 */

export type ServerState =
  | { kind: 'live' }
  | {
      kind: 'planned';
      /** The issue that builds it. */
      issue: string;
    };

export type McpServer = {
  id: string;
  name: string;
  /** Where an MCP client connects. Absent while the server does not exist. */
  address?: string;
  /**
   * What it offers: its tool names where they are few enough to list, or a
   * phrase where there are many. The page sets the two apart — a name is
   * something a client calls, a phrase is a description.
   */
  offers: { tools: string[] } | { summary: string };
  /** How a client signs in. */
  signIn: string;
  state: ServerState;
  /** Where its setup is described, for a server that can be connected today. */
  setup?: { label: string; href: string };
};

/** Where an issue lives, for a server or a tool that names one. */
export const issueUrl = (issue: string) =>
  `https://linear.app/revenexx/issue/${issue}`;

/**
 * The portal's own tools that are planned rather than offered. One is shown
 * as Live the moment the endpoint lists it, whatever this says.
 */
export const PLANNED_PORTAL_TOOLS: { name: string; issue: string }[] = [
  { name: 'adr_search', issue: 'DX-310' },
];

/** Where the portal's own server is set up — the integration guide. */
export const PORTAL_SETUP =
  '/docs/default/component/backstage/mcp-integrations/';

export const OTHER_SERVERS: McpServer[] = [
  {
    id: 'integration-studio',
    name: 'Integration Studio',
    address: 'https://integrations.revenexx.com/mcp/workflows',
    offers: { summary: "A tenant's workflows, triggers and nodes — 20 tools" },
    signIn: 'ZITADEL · tenant',
    state: { kind: 'live' },
    setup: {
      label: 'Workflow MCP server',
      href: '/docs/default/component/integrations/api/workflow-mcp/',
    },
  },
  {
    id: 'developer-portal',
    name: 'Developer Portal (public)',
    address: 'https://revenexx.dev/mcp',
    offers: {
      tools: [
        'search_docs',
        'get_page',
        'get_page_section',
        'list_sections',
        'list_pages',
      ],
    },
    signIn: 'Public',
    state: { kind: 'live' },
    setup: {
      label: 'revenexx.dev',
      href: 'https://revenexx.dev/docs/tools/mcp',
    },
  },
  {
    id: 'brand',
    name: 'revenexx Brand',
    address: 'https://brand.revenexx.design/mcpserver',
    offers: {
      summary: 'Brand guidelines, design tokens, logos and typography',
    },
    signIn: 'ZITADEL',
    state: { kind: 'live' },
    setup: {
      label: 'brand.revenexx.design',
      href: 'https://brand.revenexx.design/mcp',
    },
  },
  {
    id: 'rooms',
    name: 'Rooms',
    address: 'https://rooms.revenexx.com/mcp',
    offers: {
      summary: 'Sales rooms — their next steps, milestones and documents',
    },
    signIn: 'ZITADEL',
    state: { kind: 'live' },
    setup: {
      label: 'claude.ai connector',
      href: 'https://claude.ai/settings/connectors',
    },
  },
  {
    id: 'analytics-studio',
    name: 'Analytics Studio',
    address: 'https://mcp.analytics.revenexx.com/mcp',
    offers: {
      tools: [
        'whoami',
        'list_datasets',
        'query_view',
        'aggregate_view',
        'run_sql',
      ],
    },
    signIn: 'ZITADEL · tenant',
    state: { kind: 'live' },
    setup: {
      label: 'claude.ai connector',
      href: 'https://claude.ai/settings/connectors',
    },
  },
  {
    id: 'grafana',
    name: 'Grafana / VictoriaLogs',
    offers: { summary: 'Dashboards, metrics and logs — read only' },
    signIn: '—',
    state: { kind: 'planned', issue: 'PE-834' },
  },
  {
    id: 'status',
    name: 'Status',
    offers: { summary: 'Incidents, probes and maintenance' },
    signIn: '—',
    state: { kind: 'planned', issue: 'DX-521' },
  },
];
